import { Howl, Howler } from 'howler'
import { MathUtils } from 'three'

interface ISoundMapItem {
  id: string
  file: string
  loop?: boolean
  volume?: number
  randomVolume?: number
}

class SoundManager {
  soundMaps: Map<string, ISoundMapItem[]> = new Map<string, ISoundMapItem[]>()

  #ambienceChannel1: Howl | undefined
  #ambienceChannel2: Howl | undefined
  #currentAmbientIds: string[] = []

  constructor() {
    Howler.volume(0.3)
  }

  async registerSoundMap(id: string, map: any) {
    //cache important sounds?
    this.soundMaps.set(id, map as ISoundMapItem[])
  }

  unregisterSoundMap(id: string) {
    //TODO: unload from cache
    this.soundMaps.delete(id)
  }

  setSoundVolume(howl: Howl, volume: number) {
    if (Howler.ctx.state === 'running') {
      howl.volume(volume)
    }
  }

  changeAmbience(ids: string[]) {
    if (this.#currentAmbientIds.join('') === ids.join('')) {
      return
    }
    this.#currentAmbientIds = ids.concat()

    if (this.#ambienceChannel1) {
      this.#ambienceChannel1.off('fade')
      this.#ambienceChannel1.once('fade', () => {
        if (ids.length > 0) {
          this.#ambienceChannel1 = this.play(ids[0])
          this.#ambienceChannel1.fade(0, this.#ambienceChannel1.volume(), 400)
        } else {
          this.#ambienceChannel1 = undefined
        }
      })
      this.#ambienceChannel1.fade(this.#ambienceChannel1.volume(), 0, 400)
    } else {
      if (ids.length > 0) {
        this.#ambienceChannel1 = this.play(ids[0])
        this.#ambienceChannel1.fade(0, this.#ambienceChannel1.volume(), 400)
      }
    }

    if (this.#ambienceChannel2) {
      this.#ambienceChannel2.off('fade')
      this.#ambienceChannel2.once('fade', () => {
        if (ids.length > 1) {
          this.#ambienceChannel2 = this.play(ids[1])
          this.#ambienceChannel2.fade(0, this.#ambienceChannel2.volume(), 400)
        } else {
          this.#ambienceChannel2 = undefined
        }
      })
      this.#ambienceChannel2.fade(this.#ambienceChannel2.volume(), 0, 400)
    } else {
      if (ids.length > 1) {
        this.#ambienceChannel2 = this.play(ids[1])
        this.#ambienceChannel2.fade(0, this.#ambienceChannel2.volume(), 400)
      }
    }
  }

  play(id: string, rate = 1): Howl {
    let soundUrl = `sounds/${id}.mp3`
    let loop = false
    let volume = 1
    const soundMapItem = this.getMapItem(id)
    if (soundMapItem) {
      soundUrl = `sounds/${soundMapItem.file}.mp3`
      if (soundMapItem.loop) {
        loop = true
      }

      if (soundMapItem.volume) {
        volume = soundMapItem.volume
      }

      if (soundMapItem.randomVolume) {
        volume *= 1 - Math.random() * soundMapItem.randomVolume
      }

      volume = MathUtils.clamp(volume, 0, 1)
    } else {
      console.log('no sound in map with id:' + id)
    }

    const h = new Howl({
      src: soundUrl,
      rate: rate,
      loop,
      volume: volume
    })

    h.play()

    return h
  }

  startLoop(id: string): Howl {
    let soundUrl = `sounds/${id}.wav`

    let volume = 0

    const soundMapItem = this.getMapItem(id)
    if (soundMapItem) {
      soundUrl = `sounds/${soundMapItem.file}.mp3`

      if (soundMapItem.volume) {
        volume = soundMapItem.volume
      }
    } else {
      console.log('no sound in map with id:' + id)
    }

    const h = new Howl({
      src: soundUrl,
      loop: true,
      volume: volume
    })

    h.play()

    return h
  }

  private getMapItem(id: string): ISoundMapItem | undefined {
    let foundItem = undefined
    this.soundMaps.forEach((value: ISoundMapItem[]) => {
      const searchItem = value.find(x => x.id === id)
      if (searchItem) {
        foundItem = searchItem
      }
    })
    return foundItem
  }

  private onAppActiveChanged = (isActive: boolean) => {
    if (isActive) {
      Howler.mute(false)
    } else {
      Howler.mute(true)
    }
  }
}

export default new SoundManager()
